import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner'
import { getBlendBank, deleteBlend } from '../services/api'
import { useMixerStore } from '../store/mixerStore'
import './BlendBankPage.css'

function BlendBankPage() {
  const navigate = useNavigate()
  const { setDeckA, setDeckB } = useMixerStore()

  const [blends, setBlends] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadBlends()
  }, [])

  const loadBlends = async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await getBlendBank()
      setBlends(data)
    } catch (err) {
      setError('Failed to load saved blends')
      console.error('Load blends error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleLoadBlend = (blend) => {
    // Load both tracks into mixer with saved settings
    setDeckA({
      track: {
        id: blend.trackA.id,
        title: blend.trackA.title,
        artist: blend.trackA.artist
      },
      cuePoint: blend.trackA.cuePoint,
      eq: blend.trackA.eq
    })

    setDeckB({
      track: {
        id: blend.trackB.id,
        title: blend.trackB.title,
        artist: blend.trackB.artist
      },
      cuePoint: blend.trackB.cuePoint,
      eq: blend.trackB.eq
    })

    navigate('/mixer')
  }

  const handleDeleteBlend = async (blendId) => {
    if (!confirm('Are you sure you want to delete this blend?')) return

    try {
      await deleteBlend(blendId)
      setBlends(blends.filter(b => b.id !== blendId))
    } catch (err) {
      alert('Failed to delete blend')
      console.error('Delete blend error:', err)
    }
  }

  if (loading) {
    return (
      <div className="blend-bank-page loading">
        <LoadingSpinner size="large" />
        <p>Loading your saved blends...</p>
      </div>
    )
  }

  return (
    <div className="blend-bank-page">
      <div className="page-header">
        <h2>Blend Bank</h2>
        <p>Your saved blend combinations</p>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {blends.length === 0 && !loading && (
        <div className="empty-state">
          <h3>No saved blends yet</h3>
          <p>Create blends in the mixer and save them here for quick access</p>
          <button
            className="cta-button"
            onClick={() => navigate('/')}
          >
            Start Searching for Tracks
          </button>
        </div>
      )}

      {blends.length > 0 && (
        <div className="blends-grid">
          {blends.map(blend => (
            <div key={blend.id} className="blend-card">
              <div className="blend-header">
                <h3>{blend.name}</h3>
                <span className="blend-date">
                  {new Date(blend.created).toLocaleDateString()}
                </span>
              </div>

              <div className="blend-tracks">
                <div className="blend-track">
                  <div className="track-label">Deck A</div>
                  <div className="track-name">{blend.trackA.title}</div>
                  <div className="track-artist">{blend.trackA.artist}</div>
                  <div className="track-settings">
                    <span className="setting">Cue: {blend.trackA.cuePoint.toFixed(1)}s</span>
                    <span className="setting eq-indicator">
                      LOW: {blend.trackA.eq.low === 0 ? 'KILL' : `${blend.trackA.eq.low}%`}
                    </span>
                  </div>
                </div>

                <div className="blend-divider">+</div>

                <div className="blend-track">
                  <div className="track-label">Deck B</div>
                  <div className="track-name">{blend.trackB.title}</div>
                  <div className="track-artist">{blend.trackB.artist}</div>
                  <div className="track-settings">
                    <span className="setting">Cue: {blend.trackB.cuePoint.toFixed(1)}s</span>
                    <span className="setting eq-indicator">
                      LOW: {blend.trackB.eq.low === 0 ? 'KILL' : `${blend.trackB.eq.low}%`}
                    </span>
                  </div>
                </div>
              </div>

              {blend.notes && (
                <div className="blend-notes">
                  <strong>Notes:</strong> {blend.notes}
                </div>
              )}

              <div className="blend-actions">
                <button
                  className="btn-load"
                  onClick={() => handleLoadBlend(blend)}
                >
                  Load in Mixer
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteBlend(blend.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default BlendBankPage
